import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

/**
 * The `beforeinstallprompt` event is not in the DOM library because it is not a standard,
 * so the two members the promotion actually uses are declared here.
 */
export type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallPromotionMode =
  /** Nothing to offer: already installed, already dismissed, or nothing to install. */
  | 'none'
  /** The browser offered a native installation prompt and it is being held for the button. */
  | 'native'
  /** No native prompt exists here, so platform specific instructions are shown instead. */
  | 'manual'

const DISMISSAL_STORAGE_KEY = 'threadline.installPromotionDismissed'

/**
 * The dismissal must survive navigation inside the session without ever outliving it.
 * `sessionStorage` is exactly that lifetime. It never holds anything but this flag:
 * authentication material stays in the HTTP-only session cookie.
 */
function readDismissalFromSessionStorage(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSAL_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function persistDismissalInSessionStorage(): void {
  try {
    window.sessionStorage.setItem(DISMISSAL_STORAGE_KEY, 'true')
  } catch {
    // A browser with storage disabled simply forgets the dismissal on the next navigation.
  }
}

/** Already installed applications must never be asked to install themselves again. */
export function applicationRunsInStandaloneMode(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const standaloneNavigator = window.navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: window-controls-overlay)').matches ||
    standaloneNavigator.standalone === true
  )
}

/**
 * Chromium based browsers expose `onbeforeinstallprompt`; Safari and Firefox do not.
 * Reading the property rather than waiting for the event means Chromium never flashes the
 * manual instructions during the moment before the event fires.
 */
export function browserSupportsInstallPrompt(): boolean {
  return typeof window !== 'undefined' && 'onbeforeinstallprompt' in window
}

/** iOS installs exclusively through the Safari share sheet, which needs its own wording. */
export function runningOnIosDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  const userAgent = navigator.userAgent
  const isIpadOnDesktopUserAgent =
    /Macintosh/i.test(userAgent) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1
  return /iPhone|iPad|iPod/i.test(userAgent) || isIpadOnDesktopUserAgent
}

/**
 * Manual instructions are only honest where the platform really can install a web
 * application by hand: iOS and iPadOS Safari through the share sheet, and mobile browsers
 * with an "Add to Home Screen" action. Desktop Firefox has no installation at all, so it
 * is told nothing rather than being sent looking for a menu entry that does not exist.
 */
export function platformSupportsManualInstallation(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  return runningOnIosDevice() || /Android|Mobile/i.test(navigator.userAgent)
}

export function useInstallPromotion() {
  const retainedInstallPrompt = ref<InstallPromptEvent | null>(null)
  const wasInstalled = ref(false)
  const wasDismissed = ref(false)
  const isPrompting = ref(false)

  function handleBeforeInstallPrompt(browserEvent: Event): void {
    // Suppressing the browser bar is what allows a localized in-app promotion instead.
    browserEvent.preventDefault()
    retainedInstallPrompt.value = browserEvent as InstallPromptEvent
  }

  function handleAppInstalled(): void {
    wasInstalled.value = true
    retainedInstallPrompt.value = null
  }

  onMounted(() => {
    wasDismissed.value = readDismissalFromSessionStorage()
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleAppInstalled)
  })

  const promotionMode = computed<InstallPromotionMode>(() => {
    if (wasInstalled.value || wasDismissed.value || applicationRunsInStandaloneMode()) {
      return 'none'
    }
    if (retainedInstallPrompt.value !== null) {
      return 'native'
    }
    if (browserSupportsInstallPrompt() || !platformSupportsManualInstallation()) {
      return 'none'
    }
    return 'manual'
  })

  /**
   * Only ever reached from the install button. The native prompt is never invoked on the
   * browser's own initiative, which browsers reject and people experience as a hijack.
   */
  async function triggerInstall(): Promise<void> {
    const installPrompt = retainedInstallPrompt.value
    if (installPrompt === null || isPrompting.value) {
      return
    }

    isPrompting.value = true
    try {
      await installPrompt.prompt()
      const installChoice = await installPrompt.userChoice
      if (installChoice.outcome === 'dismissed') {
        persistDismissalInSessionStorage()
        wasDismissed.value = true
      }
    } catch (promptFailure) {
      console.warn('The installation prompt could not be shown', promptFailure)
    } finally {
      // A prompt can only be shown once, whatever the person chose.
      retainedInstallPrompt.value = null
      isPrompting.value = false
    }
  }

  function dismissPromotion(): void {
    persistDismissalInSessionStorage()
    wasDismissed.value = true
  }

  return {
    promotionMode,
    isPrompting: computed(() => isPrompting.value),
    showsIosInstructions: computed(() => promotionMode.value === 'manual' && runningOnIosDevice()),
    triggerInstall,
    dismissPromotion,
  }
}
